import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from './ui/Dialog';

/**
 * Keyboard shortcuts (spec §3.D + §6.3). Two layers:
 *
 *   1. Global handler — listens for `?` to open the cheatsheet dialog
 *      and Escape to close it. Skips typing events when the focus is
 *      inside an input / textarea / contenteditable.
 *
 *   2. Page-level `useShortcut(key, handler, label)` — pages register
 *      their own shortcuts which automatically appear in the cheatsheet.
 *
 * Example:
 *   useShortcut('e', () => assign(), 'Assign owner');
 *   useShortcut('j', () => nextRow(),  'Next row');
 */
interface ShortcutEntry {
  key: string;     // e.g. 'e', 'j', '/'
  label: string;
}

interface Api {
  register: (key: string, handler: (e: KeyboardEvent) => void, label: string) => () => void;
}

const Ctx = createContext<Api | null>(null);

export function KeyboardShortcutsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [handlers] = useState<Map<string, (e: KeyboardEvent) => void>>(() => new Map());
  const [entries, setEntries] = useState<ShortcutEntry[]>([]);

  // Built-in entries that always appear in the cheatsheet, even before
  // a page registers its own. Pages can override by re-registering 'j'/'k'.
  const baseEntries: ShortcutEntry[] = useMemo(() => ([
    { key: '?', label: t('shortcuts.openCheatsheet') },
    { key: 'g i', label: t('shortcuts.goInbox') },
    { key: 'g a', label: t('shortcuts.goAp') },
    { key: 'g v', label: t('shortcuts.goVendors') },
    { key: 'g r', label: t('shortcuts.goReports') },
    { key: 'g s', label: t('shortcuts.goSettings') },
  ]), [t]);

  const register = useCallback((key: string, handler: (e: KeyboardEvent) => void, label: string) => {
    handlers.set(key, handler);
    setEntries((prev) => prev.find((e) => e.key === key) ? prev : [...prev, { key, label }]);
    return () => {
      handlers.delete(key);
      setEntries((prev) => prev.filter((e) => e.key !== key));
    };
  }, [handlers]);

  useEffect(() => {
    let gPressed = false;
    let gTimer: number | null = null;

    function inEditable(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      const tag = t.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t.isContentEditable === true);
    }

    function onKey(e: KeyboardEvent) {
      if (inEditable(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
        return;
      }

      // Two-key "g x" navigation prefix.
      if (e.key === 'g' && !gPressed) {
        gPressed = true;
        if (gTimer) window.clearTimeout(gTimer);
        gTimer = window.setTimeout(() => { gPressed = false; }, 800);
        return;
      }
      if (gPressed) {
        const wsId = window.location.pathname.match(/\/workspace\/([^/]+)/)?.[1];
        if (!wsId) { gPressed = false; return; }
        gPressed = false;
        const dest: Record<string, string> = {
          i: 'inbox', a: 'ap-invoices', v: 'vendors', r: 'reports', s: 'settings',
        };
        const path = dest[e.key];
        if (path) {
          e.preventDefault();
          window.history.pushState({}, '', `/app/workspace/${wsId}/${path}`);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
        return;
      }

      // Page-level handler dispatch.
      const fn = handlers.get(e.key);
      if (fn) fn(e);
    }

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (gTimer) window.clearTimeout(gTimer);
    };
  }, [handlers, open]);

  const merged: ShortcutEntry[] = useMemo(() => {
    const map = new Map<string, string>();
    baseEntries.forEach((e) => map.set(e.key, e.label));
    entries.forEach((e) => map.set(e.key, e.label));
    return Array.from(map.entries()).map(([key, label]) => ({ key, label }));
  }, [baseEntries, entries]);

  return (
    <Ctx.Provider value={{ register }}>
      {children}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('shortcuts.title')}
        description={t('shortcuts.subtitle')}
      >
        <ul className="divide-y divide-ink-200 dark:divide-ink-800 text-sm">
          {merged.map((e) => (
            <li key={e.key} className="flex items-center justify-between py-2">
              <span>{e.label}</span>
              <kbd className="px-2 py-0.5 rounded border border-ink-300 dark:border-ink-700 bg-ink-50 dark:bg-ink-900 font-mono text-xs">
                {e.key}
              </kbd>
            </li>
          ))}
        </ul>
      </Dialog>
    </Ctx.Provider>
  );
}

export function useShortcut(key: string, handler: (e: KeyboardEvent) => void, label: string): void {
  const ctx = useContext(Ctx);
  useEffect(() => {
    if (!ctx) return;
    return ctx.register(key, handler, label);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, label]);
}
